import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMyConnections, getMyPerformance, ALL_ACCOUNTS } from '../dashboard/myData/myDataApi';
import { futuresStatus, stocksStatus, formatSpan } from './marketHours';
import {
  IconAlert, IconArrowRight, IconBook, IconHedge, IconLock, IconZap, IconCheck, IconChevronLeft, IconChevronRight,
} from './welcomeIcons';
import { buildConnectItems, totalBrokerCount } from './connectSources';
import { fetchSnaptradeAvailability, fetchSnaptradeBrokerages, snaptradeConnectLink } from '../../services/vectorDashboardApi';
import DashboardPreview from './DashboardPreview';
import './WelcomeOverview.css';

/**
 * Welcome Overview for self users (no company): a live hero with market hours
 * and an account snapshot, a rotating risk tip, every way to bring in your own
 * data, your dashboards, and quick tools.
 *
 * The page is free; connecting accounts and personal dashboards are RiskLo Pro,
 * so free users see the cards locked with an upgrade button.
 *
 * The snapshot shows the user's own accounts when they have history, and a
 * clearly labelled example account otherwise.
 *
 * `dashboards` is rendered for Pro users (DashboardPageInner in personal mode).
 *
 * The full welcome is for getting started: step 1 connect accounts, step 2
 * build a dashboard from them. Once the user has a dashboard (`hasDashboards`) the page
 * steps back to a slim greeting bar that offers the accounts not yet connected,
 * with the dashboard below. The dashboard keeps its place in the tree either
 * way, so it never reloads when the page switches mode.
 */

const PORTFOLIO_SOURCES = ['ninjatrader', 'snaptrade', 'alpaca', 'coinbase', 'ibkr'];

/** Broker logo (white tile) or a colored letter mark. */
function SourceMark({ item, className }) {
  return (
    <span className={`${className}${item.logo ? ' has-logo' : ''} wo-hue-${item.hue}`} aria-hidden="true">
      {item.logo ? <img src={item.logo} alt="" loading="lazy" /> : item.mark}
    </span>
  );
}

const TOOLS = [
  { title: 'Blowout Risk', blurb: 'Will this setup blow your prop account?', go: 'home', Icon: IconAlert, hue: 'red' },
  { title: 'Hedging Risk', blurb: 'How two strategies offset each other.', go: 'hedging-risk', Icon: IconHedge, hue: 'blue' },
  { title: 'Trade Watcher', blurb: 'Your NinjaTrader accounts, live.', go: 'trade-watcher', Icon: IconZap, hue: 'violet' },
  { title: 'Tutorials', blurb: 'Short videos to get the most out of RiskLo.', go: 'tutorials', Icon: IconBook, hue: 'teal' },
];

const TAGLINES = [
  'Every account, one view.',
  'Know your risk before the market does.',
  'See the drawdown coming, not after.',
  'Your P&L, your rules, your dashboard.',
];

const TIPS = [
  { title: 'Drawdown trails your best day', text: 'Most prop firms measure drawdown from your highest balance, not your starting one. Run Blowout Risk before you size up.', go: 'home', cta: 'Check blowout risk' },
  { title: 'Plan for the streak, not the average', text: 'A strategy that wins 60% of the time can still lose 8 in a row. Size for the losing streak you will eventually hit.', go: 'home', cta: 'Model a losing streak' },
  { title: 'Naked positions blow accounts', text: 'An open position with no stop working is the fastest way to lose an account. Live NinjaTrader alerts warn you the moment one appears.', go: 'trade-watcher', cta: 'Set up live alerts' },
  { title: 'Two strategies, same bad days?', text: 'Strategies that lose on the same days don’t spread your risk. Hedging Risk shows how much they overlap.', go: 'hedging-risk', cta: 'Compare strategies' },
  { title: 'Watch your consistency', text: 'Many prop firms cap how much of your profit can come from one day. Keep your best day in check before payout.', go: 'apex-30-percent-rule', cta: 'Check the % rule' },
];

const RANGES = [
  { id: '1M', label: '1M', days: 22 },
  { id: '3M', label: '3M', days: 64 },
  { id: '6M', label: '6M', days: 128 },
];

/* Helpers -------------------------------------------------------------- */

function greeting(d = new Date()) {
  const h = d.getHours();
  if (h < 5) return 'Up late';
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function firstName(user) {
  const name = String(user?.name || user?.firstName || '').trim();
  if (name) return name.split(/\s+/)[0];
  return String(user?.email || '').split('@')[0] || 'there';
}

const money = (n, { sign = false } = {}) => {
  const v = Number(n) || 0;
  const s = Math.abs(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  if (!sign) return v < 0 ? `-${s}` : s;
  return `${v > 0 ? '+' : v < 0 ? '-' : ''}${s}`;
};

/** Small deterministic random numbers, so the example account looks the same each visit. */
function seeded(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Example futures account: a gently rising equity curve with real-looking pullbacks. */
function exampleSeries(days, seed = 7) {
  const rnd = seeded(seed + days);
  const pts = [];
  let v = 50000;
  const today = new Date();
  for (let i = days; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(today.getDate() - Math.round(i * 1.4));
    v += (rnd() - 0.43) * 900;
    pts.push({ date: d.toISOString().slice(0, 10), value: Math.round(v) });
  }
  return pts;
}

function summarize(points) {
  if (points.length < 2) return null;
  const daily = points.slice(1).map((p, i) => p.value - points[i].value);
  const wins = daily.filter((d) => d > 0).length;
  let peak = points[0].value;
  let dd = 0;
  points.forEach((p) => { peak = Math.max(peak, p.value); dd = Math.min(dd, p.value - peak); });
  return {
    equity: points[points.length - 1].value,
    change: points[points.length - 1].value - points[0].value,
    winRate: daily.length ? wins / daily.length : 0,
    best: Math.max(...daily),
    drawdown: dd,
  };
}

/* Hooks ---------------------------------------------------------------- */

function useReducedMotion() {
  const [reduced, setReduced] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  ));
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return undefined;
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return reduced;
}

/** Animate a number toward its target (eased), from wherever it currently is. */
function useCountUp(target, { duration = 900, reduced = false } = {}) {
  const [value, setValue] = useState(reduced ? target : 0);
  const fromRef = useRef(reduced ? target : 0);
  useEffect(() => {
    if (reduced || !Number.isFinite(target)) { setValue(target); fromRef.current = target; return undefined; }
    const from = fromRef.current;
    const start = performance.now();
    let raf = 0;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      const v = from + (target - from) * eased;
      fromRef.current = v;
      setValue(v);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, reduced]);
  return value;
}

/** Adds .is-in when the element scrolls into view (once). */
function useReveal() {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setShown(true); return undefined; }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShown(true); io.disconnect(); }
    }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, shown ? ' is-in' : ''];
}

function useNow(everyMs = 30000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), everyMs);
    return () => clearInterval(id);
  }, [everyMs]);
  return now;
}

/** Cursor-following glow: sets --mx/--my on the element. */
function spotlight(e) {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
}

/* Pieces --------------------------------------------------------------- */

function MarketChip({ label, status }) {
  return (
    <span className={`wo-chip${status.open ? ' is-open' : ''}`} title="Regular hours, New York time. Exchange holidays not included.">
      <span className="wo-chip-dot" aria-hidden="true" />
      <strong>{label}</strong>
      <span>
        {status.open ? 'open' : 'closed'} · <span className="wo-chip-long">{status.open ? 'closes' : 'opens'} in </span>{formatSpan(status.minutesLeft)}
      </span>
    </span>
  );
}

function Rotator({ items, every = 3800, reduced }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reduced) return undefined;
    const id = setInterval(() => setI((n) => (n + 1) % items.length), every);
    return () => clearInterval(id);
  }, [items.length, every, reduced]);
  return (
    <span className="wo-rotator" aria-live="polite">
      <span key={i} className="wo-rotator-item">{items[i]}</span>
    </span>
  );
}

function SetupRing({ done, total }) {
  const r = 22;
  const c = 2 * Math.PI * r;
  const pct = total ? done / total : 0;
  return (
    <div className="wo-ring" title={`${done} of ${total} sources connected`}>
      <svg viewBox="0 0 56 56" aria-hidden="true">
        <circle cx="28" cy="28" r={r} className="wo-ring-track" />
        <circle cx="28" cy="28" r={r} className="wo-ring-fill" strokeDasharray={c} strokeDashoffset={c * (1 - pct)} />
      </svg>
      <span>{done}/{total}</span>
    </div>
  );
}

/** Equity sparkline with a draw-in animation and a hover crosshair. */
function Sparkline({ points, animateKey }) {
  const [hover, setHover] = useState(null);
  const W = 600;
  const H = 170;
  const geom = useMemo(() => {
    if (points.length < 2) return null;
    const vals = points.map((p) => p.value);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const pad = (max - min) * 0.12 || 1;
    const lo = min - pad;
    const hi = max + pad;
    const x = (i) => (i / (points.length - 1)) * W;
    const y = (v) => H - ((v - lo) / (hi - lo)) * H;
    const xy = points.map((p, i) => [x(i), y(p.value)]);
    // Smooth curve through the points.
    let d = `M${xy[0][0]},${xy[0][1]}`;
    for (let i = 1; i < xy.length; i += 1) {
      const [x0, y0] = xy[i - 1];
      const [x1, y1] = xy[i];
      const mx = (x0 + x1) / 2;
      d += ` C${mx},${y0} ${mx},${y1} ${x1},${y1}`;
    }
    return { d, area: `${d} L${W},${H} L0,${H} Z`, xy, up: vals[vals.length - 1] >= vals[0] };
  }, [points]);

  if (!geom) return <div className="wo-spark wo-spark--empty" />;

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const t = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    setHover(Math.round(t * (points.length - 1)));
  };
  const hp = hover != null ? points[hover] : null;
  const hxy = hover != null ? geom.xy[hover] : null;

  return (
    <div className={`wo-spark ${geom.up ? 'is-up' : 'is-down'}`} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id="woSparkFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" className="wo-spark-stop-a" />
            <stop offset="100%" className="wo-spark-stop-b" />
          </linearGradient>
        </defs>
        <path key={`a${animateKey}`} d={geom.area} className="wo-spark-area" fill="url(#woSparkFill)" />
        <path key={`l${animateKey}`} d={geom.d} className="wo-spark-line" pathLength="1" vectorEffect="non-scaling-stroke" />
      </svg>
      {hp ? (
        <>
          <span className="wo-spark-cross" style={{ left: `${(hxy[0] / W) * 100}%` }} />
          <span className="wo-spark-dot" style={{ left: `${(hxy[0] / W) * 100}%`, top: `${(hxy[1] / H) * 100}%` }} />
          <span className={`wo-spark-tip${hxy[0] / W > 0.7 ? ' is-left' : ''}`} style={{ left: `${(hxy[0] / W) * 100}%` }}>
            <b>{money(hp.value)}</b>
            <i>{new Date(`${hp.date}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</i>
          </span>
        </>
      ) : (
        <span className="wo-spark-dot is-live" style={{ left: '100%', top: `${(geom.xy[geom.xy.length - 1][1] / H) * 100}%` }} />
      )}
    </div>
  );
}

function Stat({ label, value, format, reduced, tone }) {
  const v = useCountUp(value, { reduced });
  return (
    <div className="wo-stat">
      <span>{label}</span>
      <b className={tone ? `is-${tone}` : ''}>{format(v)}</b>
    </div>
  );
}

function Snapshot({ isPro, hasPortfolio, reduced, onPrimary }) {
  const [range, setRange] = useState('1M');
  const [real, setReal] = useState(null); // { points } for the user's own accounts
  const [tick, setTick] = useState(0);
  const cardRef = useRef(null);

  useEffect(() => {
    if (!isPro || !hasPortfolio) { setReal(null); return undefined; }
    let live = true;
    getMyPerformance(ALL_ACCOUNTS, range)
      .then((r) => { if (live) setReal(r.points && r.points.length > 1 ? { points: r.points } : null); })
      .catch(() => { if (live) setReal(null); });
    return () => { live = false; };
  }, [isPro, hasPortfolio, range]);

  // The example account "breathes": its latest value moves a little every couple of seconds.
  useEffect(() => {
    if (real || reduced) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 2200);
    return () => clearInterval(id);
  }, [real, reduced]);

  const example = !real;
  const points = useMemo(() => {
    if (real) return real.points.map((p) => ({ date: String(p.date).slice(0, 10), value: Number(p.value) || 0 }));
    const days = RANGES.find((r) => r.id === range)?.days || 22;
    const base = exampleSeries(days);
    if (!tick) return base;
    const rnd = seeded(tick * 97);
    const last = base[base.length - 1];
    return [...base.slice(0, -1), { ...last, value: Math.round(last.value + (rnd() - 0.45) * 420) }];
  }, [real, range, tick]);

  const s = summarize(points) || { equity: 0, change: 0, winRate: 0, best: 0, drawdown: 0 };
  const equity = useCountUp(s.equity, { reduced });

  // Gentle 3D tilt toward the cursor.
  const onMove = (e) => {
    if (reduced || !cardRef.current) return;
    const r = cardRef.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    cardRef.current.style.setProperty('--rx', `${(-py * 5).toFixed(2)}deg`);
    cardRef.current.style.setProperty('--ry', `${(px * 7).toFixed(2)}deg`);
  };
  const onLeave = () => {
    cardRef.current?.style.setProperty('--rx', '0deg');
    cardRef.current?.style.setProperty('--ry', '0deg');
  };

  return (
    <div className="wo-snap" ref={cardRef} onMouseMove={onMove} onMouseLeave={onLeave}>
      <div className="wo-snap-head">
        <div>
          <span className="wo-snap-label">{example ? 'Example account' : 'All your accounts'}</span>
          <div className="wo-snap-equity">{money(equity)}</div>
          <span className={`wo-snap-change ${s.change >= 0 ? 'is-up' : 'is-down'}`}>
            {s.change >= 0 ? '▲' : '▼'} {money(s.change, { sign: true })}
            <em>{' '}this {range === '1M' ? 'month' : range === '3M' ? 'quarter' : 'half'}</em>
          </span>
        </div>
        <div className="wo-snap-ranges" role="tablist" aria-label="Period">
          {RANGES.map((r) => (
            <button key={r.id} type="button" role="tab" aria-selected={range === r.id} className={range === r.id ? 'active' : ''} onClick={() => setRange(r.id)}>
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <Sparkline points={points} animateKey={`${range}-${example ? 'x' : 'r'}`} />
      <div className="wo-stats">
        <Stat label="Winning days" value={s.winRate * 100} format={(v) => `${Math.round(v)}%`} reduced={reduced} />
        <Stat label="Best day" value={s.best} format={(v) => money(v, { sign: true })} reduced={reduced} tone="up" />
        <Stat label="Max drawdown" value={s.drawdown} format={(v) => money(v)} reduced={reduced} tone="down" />
      </div>
      {example ? (
        <button type="button" className="wo-snap-foot" onClick={onPrimary}>
          <span className="wo-badge">EXAMPLE</span>
          <span>{isPro ? 'Connect an account to see your own numbers here' : 'Get RiskLo Pro to see your own numbers here'}</span>
          <IconArrowRight />
        </button>
      ) : (
        <div className="wo-snap-foot is-live"><span className="wo-chip-dot" aria-hidden="true" /> Synced from your connected accounts</div>
      )}
    </div>
  );
}

function TipStrip({ onNavigate, reduced }) {
  const [i, setI] = useState(() => new Date().getDate() % TIPS.length);
  const [paused, setPaused] = useState(false);
  const go = useCallback((d) => setI((n) => (n + d + TIPS.length) % TIPS.length), []);
  useEffect(() => {
    if (paused || reduced) return undefined;
    const id = setTimeout(() => go(1), 9000);
    return () => clearTimeout(id);
  }, [i, paused, reduced, go]);
  const tip = TIPS[i];
  return (
    <div className={`wo-tip${paused ? ' is-paused' : ''}`} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <span className="wo-tip-icon" aria-hidden="true">💡</span>
      <div key={i} className="wo-tip-body">
        <strong>{tip.title}</strong>
        <span>{tip.text}</span>
      </div>
      <button type="button" className="wo-tip-cta" onClick={() => onNavigate && onNavigate(tip.go)}>{tip.cta}</button>
      <div className="wo-tip-nav">
        <button type="button" onClick={() => go(-1)} aria-label="Previous tip"><IconChevronLeft /></button>
        <span>{i + 1}/{TIPS.length}</span>
        <button type="button" onClick={() => go(1)} aria-label="Next tip"><IconChevronRight /></button>
      </div>
      {!reduced ? <span key={`p${i}`} className="wo-tip-progress" aria-hidden="true" /> : null}
    </div>
  );
}

function CompactBar({ user, now, fut, stk, isPro, items, allBrokers, onConnect, opening, snaptradeSoon = false }) {
  const [open, setOpen] = useState(false);
  const missing = items.filter((it) => !it.connected);
  const connected = items.length - missing.length;
  return (
    <div className="wo-compact">
      <div className="wo-compact-row">
        <div className="wo-compact-hello">
          <span className="wo-date">{now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
          <h1>{greeting(now)}, <span className="wo-grad">{firstName(user)}</span></h1>
        </div>
        <div className="wo-chips">
          <MarketChip label="Futures" status={fut} />
          <MarketChip label="US stocks" status={stk} />
        </div>
        {isPro && missing.length ? (
          <button type="button" className={`wo-compact-more${open ? ' is-open' : ''}`} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            <span className="wo-compact-marks" aria-hidden="true">
              {missing.slice(0, 4).map((it) => <SourceMark key={it.key} item={it} className="wo-compact-mark" />)}
            </span>
            <span>
              <strong>Connect more accounts</strong>
              <em>{connected} connected · {snaptradeSoon ? 'more brokers coming soon' : `${allBrokers}+ brokers available`}</em>
            </span>
            <IconChevronRight />
          </button>
        ) : null}
      </div>
      {open ? (
        <div className="wo-compact-list">
          {/* Connected brokers first (Schwab, Robinhood…), then the rest. */}
          {items.slice(0, 11).map((it, idx) => (
            <button
              type="button"
              key={it.key}
              style={{ '--i': idx }}
              className={`wo-pill wo-hue-${it.hue}${it.connected ? ' is-connected' : ''}${it.comingSoon ? ' is-soon' : ''}`}
              onClick={() => onConnect(it)}
              onMouseMove={spotlight}
              disabled={Boolean(opening)}
              aria-disabled={it.comingSoon || undefined}
              title={it.comingSoon ? `${it.title} is coming soon` : it.connected ? `Manage ${it.title} or add another account` : `Connect ${it.title}`}
            >
              <span className="wo-pill-mark-wrap">
                <SourceMark item={it} className="wo-pill-mark" />
                {it.connected ? <span className="wo-source-ok"><IconCheck size={9} /></span> : null}
              </span>
              <span className="wo-pill-text">
                <strong>{it.title}</strong>
                <span>{it.comingSoon ? 'Coming soon' : opening === it.key ? 'Opening…' : it.connected ? 'Connected' : it.blurb}</span>
              </span>
              {it.comingSoon ? null : <IconArrowRight />}
            </button>
          ))}
          <button
            type="button"
            style={{ '--i': 11 }}
            className="wo-pill wo-pill--all wo-hue-indigo"
            onClick={() => onConnect({ key: 'all', action: { type: 'page', page: 'client-reporting?connect=all' } })}
            onMouseMove={spotlight}
          >
            <span className="wo-pill-mark" aria-hidden="true">+</span>
            <span className="wo-pill-text"><strong>All {allBrokers} brokers</strong><span>Search every broker you can connect</span></span>
            <IconArrowRight />
          </button>
        </div>
      ) : null}
    </div>
  );
}

/* Page ----------------------------------------------------------------- */

export default function WelcomeOverview({ user, isPro, onNavigate, onUpgrade, dashboards, hasDashboards = false }) {
  const reduced = useReducedMotion();
  const now = useNow(30000);
  const [conn, setConn] = useState(null);
  useEffect(() => {
    if (!isPro) { setConn({ pro: false }); return undefined; }
    let live = true;
    getMyConnections(user?.email).then((c) => { if (live) setConn(c); });
    return () => { live = false; };
  }, [isPro, user?.email]);

  // SnapTrade's brokerage list (live logos); Pro only, the fallbacks cover free users.
  const [brokerages, setBrokerages] = useState(null);
  useEffect(() => {
    if (!isPro) return undefined;
    let live = true;
    fetchSnaptradeBrokerages()
      .then((res) => { if (live) setBrokerages(res.brokerages || []); })
      .catch(() => { if (live) setBrokerages([]); });
    return () => { live = false; };
  }, [isPro]);
  // Whether SnapTrade linking is live yet (any tier): until RiskLo has its
  // production key, Robinhood, Schwab and the rest show "Coming soon".
  const [snaptradeAvailable, setSnaptradeAvailable] = useState(null);
  useEffect(() => {
    if (!user?.email) return undefined;
    let live = true;
    fetchSnaptradeAvailability()
      // Only an explicit "no key" from the server means Coming soon; a failed
      // check (older server, network) leaves the cards as they are.
      .then((res) => { if (live) setSnaptradeAvailable(res?.available === false ? false : true); })
      .catch(() => { if (live) setSnaptradeAvailable(null); });
    return () => { live = false; };
  }, [user?.email]);
  const items = useMemo(
    () => buildConnectItems(conn, brokerages, { snaptradeAvailable }),
    [conn, brokerages, snaptradeAvailable],
  );
  const snaptradeSoon = snaptradeAvailable === false;
  const allBrokers = totalBrokerCount(brokerages);

  const [opening, setOpening] = useState('');
  const connect = async (it) => {
    if (it.comingSoon) return;
    if (!isPro) { onUpgrade && onUpgrade(); return; }
    // Already linked through SnapTrade: open its manage panel (sync, add another account, unlink).
    if (it.connected && it.action?.type === 'snaptrade') {
      onNavigate && onNavigate('client-reporting?connect=snaptrade');
      return;
    }
    if (it.action?.type === 'snaptrade') {
      if (opening) return;
      setOpening(it.key);
      try {
        const light = document.body.classList.contains('light-mode');
        const res = await snaptradeConnectLink({ broker: it.action.slug, darkMode: !light });
        if (res?.url) { window.location.assign(res.url); return; }
      } catch (_e) { /* fall back to the picker */ }
      setOpening('');
      onNavigate && onNavigate('client-reporting?connect=all');
      return;
    }
    if (it.action?.page) onNavigate && onNavigate(it.action.page);
  };

  const [sourcesRef, sourcesIn] = useReveal();
  const [dashRef, dashIn] = useReveal();
  const [toolsRef, toolsIn] = useReveal();
  const sourcesEl = useRef(null);

  const go = (page) => () => onNavigate && onNavigate(page);
  const connectedCount = items.filter((it) => it.connected).length;
  const hasPortfolio = Boolean(conn && PORTFOLIO_SOURCES.some((k) => conn[k]));
  const fut = futuresStatus(now);
  const stk = stocksStatus(now);

  const dashEl = useRef(null);
  const scrollTo = (ref) => () => ref.current?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  const scrollToSources = scrollTo(sourcesEl);
  // Step 1 is connecting accounts (the dashboard is built from them); once one
  // is connected, the main button moves on to building the dashboard.
  const needsAccounts = isPro && connectedCount === 0;
  const primary = !isPro ? onUpgrade : needsAccounts ? scrollToSources : scrollTo(dashEl);
  const primaryLabel = !isPro ? 'Upgrade to RiskLo Pro' : needsAccounts ? 'Connect your accounts' : 'Build your dashboard';
  const compact = Boolean(isPro && hasDashboards);

  return (
    <div className={`wo-page${reduced ? ' is-reduced' : ''}${compact ? ' is-compact' : ''}`}>
      {compact ? null : (
      <section className="wo-hero">
        <div className="wo-aurora" aria-hidden="true"><span /><span /><span /></div>
        <div className="wo-hero-grid" aria-hidden="true" />
        <div className="wo-hero-main">
          <div className="wo-chips">
            <MarketChip label="Futures" status={fut} />
            <MarketChip label="US stocks" status={stk} />
          </div>
          <p className="wo-date">{now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
          <h1>
            {greeting(now)},{' '}
            <span className="wo-grad">{firstName(user)}</span>
          </h1>
          <p className="wo-lede"><Rotator items={TAGLINES} reduced={reduced} /></p>
          <div className="wo-cta">
            {isPro ? (
              // Same order as the steps; the glowing one is the step you're on.
              <>
                <button type="button" className={`wo-btn ${needsAccounts ? 'wo-btn--glow' : 'wo-btn--ghost'}`} onClick={scrollToSources}>
                  Connect accounts {needsAccounts ? <IconArrowRight /> : null}
                </button>
                <button type="button" className={`wo-btn ${needsAccounts ? 'wo-btn--ghost' : 'wo-btn--glow'}`} onClick={scrollTo(dashEl)}>
                  Build your dashboard {needsAccounts ? null : <IconArrowRight />}
                </button>
              </>
            ) : (
              <>
                <button type="button" className="wo-btn wo-btn--glow" onClick={primary}>
                  {primaryLabel} <IconArrowRight />
                </button>
                <button type="button" className="wo-btn wo-btn--ghost" onClick={go('home')}>Check blowout risk</button>
              </>
            )}
          </div>
          <ol className="wo-steps">
            <li className={needsAccounts ? 'is-current' : ''}>
              <SetupRing done={connectedCount} total={Math.max(connectedCount, 3)} />
              <div>
                <strong>Connect your accounts</strong>
                <span>
                  {connectedCount
                    ? `${connectedCount} connected. Add more and they all roll into one view.`
                    : 'NinjaTrader, Schwab, Robinhood, Coinbase, IBKR or a CSV.'}
                </span>
              </div>
            </li>
            <li className={needsAccounts ? '' : 'is-current'}>
              <span className="wo-step-num">2</span>
              <div>
                <strong>Build your dashboard</strong>
                <span>Pick a starter layout. Takes a few seconds.</span>
              </div>
            </li>
          </ol>
        </div>
        <Snapshot isPro={isPro} hasPortfolio={hasPortfolio} reduced={reduced} onPrimary={isPro ? scrollToSources : onUpgrade} />
      </section>
      )}

      {compact ? null : <TipStrip onNavigate={onNavigate} reduced={reduced} />}

      {compact ? (
        <CompactBar
          user={user}
          now={now}
          fut={fut}
          stk={stk}
          isPro={isPro}
          items={items}
          allBrokers={allBrokers}
          snaptradeSoon={snaptradeSoon}
          onConnect={connect}
          opening={opening}
        />
      ) : null}


      {compact ? null : (
      <section className={`wo-section wo-reveal${sourcesIn}`} ref={(el) => { sourcesRef.current = el; sourcesEl.current = el; }}>
        <div className="wo-section-head">
          <h2>{isPro ? 'Step 1 · Connect your accounts' : 'Connect your accounts'}</h2>
          {!isPro ? <span className="wo-pro-tag">PRO</span> : null}
          <span className="wo-section-sub">Read-only. RiskLo never places trades.</span>
        </div>
        <div className="wo-sources">
          {items.slice(0, 11).map((it, idx) => {
            const soon = Boolean(it.comingSoon);
            const locked = !isPro && !soon;
            return (
              <button
                type="button"
                key={it.key}
                style={{ '--i': idx }}
                className={`wo-source wo-hue-${it.hue}${it.connected ? ' is-connected' : ''}${locked ? ' is-locked' : ''}${soon ? ' is-soon' : ''}`}
                onClick={() => connect(it)}
                onMouseMove={spotlight}
                disabled={Boolean(opening)}
                aria-disabled={soon || undefined}
                title={soon ? `${it.title} is coming soon` : locked ? 'Part of RiskLo Pro' : undefined}
              >
                <span className="wo-source-mark-wrap">
                  <SourceMark item={it} className="wo-source-mark" />
                  {it.connected ? <span className="wo-source-ok"><IconCheck size={10} /></span> : null}
                </span>
                <span className="wo-source-text">
                  <strong>{it.title}</strong>
                  <span>{it.blurb}</span>
                </span>
                <span className="wo-source-state">
                  {soon ? 'Coming soon'
                    : locked ? <><IconLock size={12} /> Pro</>
                      : opening === it.key ? 'Opening…'
                        : it.connected ? 'Connected' : it.key === 'csv' ? 'Upload' : 'Connect'}
                  {!soon && !locked && !it.connected && opening !== it.key ? <IconArrowRight /> : null}
                </span>
              </button>
            );
          })}
          <button
            type="button"
            style={{ '--i': 11 }}
            className={`wo-source wo-source--all wo-hue-indigo${!isPro && !snaptradeSoon ? ' is-locked' : ''}${!isPro && snaptradeSoon ? ' is-soon' : ''}`}
            onClick={() => connect({ key: 'all', comingSoon: !isPro && snaptradeSoon, action: { type: 'page', page: 'client-reporting?connect=all' } })}
            onMouseMove={spotlight}
          >
            <span className="wo-source-mark-wrap"><span className="wo-source-mark wo-hue-indigo" aria-hidden="true">+</span></span>
            <span className="wo-source-text">
              <strong>All {allBrokers} brokers</strong>
              <span>Fidelity, Vanguard, Public, tastytrade, Kraken and more. Search them all.</span>
            </span>
            <span className="wo-source-state">
              {!isPro && snaptradeSoon ? 'Coming soon' : !isPro ? <><IconLock size={12} /> Pro</> : <>Browse <IconArrowRight /></>}
            </span>
          </button>
        </div>
      </section>
      )}

      <section
        className={`wo-section wo-reveal${compact ? ' is-in' : dashIn}`}
        ref={(el) => { dashRef.current = el; dashEl.current = el; }}
      >
        {compact ? null : (
          <div className="wo-section-head">
            <h2>{isPro ? 'Step 2 · Build your dashboard' : 'Your dashboards'}</h2>
            {!isPro ? <span className="wo-pro-tag">PRO</span> : null}
          </div>
        )}
        {isPro ? (
          dashboards
        ) : (
          <div className="wo-locked-dash">
            <div className="wo-locked-preview" aria-hidden="true">
              <DashboardPreview />
            </div>
            <span className="wo-locked-sample" aria-hidden="true">Example dashboard</span>
            <div className="wo-locked-overlay">
              <div className="wo-locked-card">
              <span className="wo-locked-icon" aria-hidden="true"><IconLock size={20} /></span>
              <h3>Your own dashboard, your way</h3>
              <p>Equity curve, P&amp;L calendar, live NinjaTrader accounts, positions and more, from your own accounts. Drag, resize and arrange it however you like.</p>
              <button type="button" className="wo-btn wo-btn--glow" onClick={() => onUpgrade && onUpgrade()}>Upgrade to RiskLo Pro <IconArrowRight /></button>
              </div>
            </div>
          </div>
        )}
      </section>

      {compact ? null : (
      <section className={`wo-section wo-reveal${toolsIn}`} ref={toolsRef}>
        <div className="wo-section-head"><h2>Tools</h2></div>
        <div className="wo-tools">
          {TOOLS.map((t, idx) => (
            <button
              type="button"
              key={t.title}
              style={{ '--i': idx }}
              className={`wo-tool wo-hue-${t.hue}`}
              onClick={go(t.go)}
              onMouseMove={spotlight}
            >
              <span className="wo-tool-icon" aria-hidden="true"><t.Icon size={20} /></span>
              <strong>{t.title}</strong>
              <span>{t.blurb}</span>
              <span className="wo-tool-go" aria-hidden="true"><IconArrowRight /></span>
            </button>
          ))}
        </div>
      </section>
      )}
    </div>
  );
}
